import { useEffect, useRef, useState } from "react";
import { vLabel } from "@/lib/version";
import { toast } from "sonner";
import { AlertTriangle, BookText, Loader2, Paperclip, Send, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { usePrompts } from "@/components/admin/PromptLibrary";
import { TechInfo } from "@/components/admin/TechInfo";
import { useSpace } from "@/context/SpaceContext";
import { TranslationsPanel } from "@/components/admin/TranslationsPanel";

function insertAt(el, current, snippet) {
  const start = el?.selectionStart ?? current.length;
  const end = el?.selectionEnd ?? current.length;
  const before = current.slice(0, start);
  const sep = before && !before.endsWith("\n") ? "\n" : "";
  return before + sep + snippet + current.slice(end);
}

const AiBlock = ({ versionId, onDraft, t }) => {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const generate = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/admin/versions/${versionId}/generate`, { notes });
      onDraft(data.draft);
      toast.success(t("ai.draftReady"));
    } catch {
      setError(t("ai.error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2 rounded-xl border border-[rgba(245,197,66,0.25)] bg-[rgba(74,6,11,0.35)] p-3" data-testid="ai-block">
      <Label htmlFor="ai-notes">{t("ai.notes")}</Label>
      <Textarea id="ai-notes" rows={3} value={notes} placeholder={t("ai.notesPlaceholder")} onChange={(e) => setNotes(e.target.value)} data-testid="ai-notes-input" />
      <button type="button" className="btn-gold btn-sm" onClick={generate} disabled={busy} data-testid="ai-generate-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t(busy ? "ai.generating" : "ai.generate")}
      </button>
      {error && <p className="flex items-start gap-2 text-sm text-red-200" role="alert" data-testid="ai-error"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}</p>}
    </div>
  );
};

export const DescriptionDialog = ({ version, onClose, onPublished }) => {
  const { t, locale } = useI18n();
  const { prompts } = usePrompts();
  const { base, isDev } = useSpace();
  const [desc, setDesc] = useState(null);
  const [text, setText] = useState("");
  const [ai, setAi] = useState(false);
  const [fileError, setFileError] = useState("");
  const [saving, setSaving] = useState(false);
  const area = useRef(null);
  const fileInput = useRef(null);
  const touched = useRef(false);
  const edit = (next) => {
    touched.current = true;
    setText(next);
  };

  useEffect(() => {
    touched.current = false;
    api.get(`${base}/versions/${version.id}/description`).then(({ data }) => {
      setDesc(data);
      if (!touched.current) setText(data.text || "");
    });
    if (!isDev) api.get("/admin/settings/ai").then(({ data }) => setAi(data.enabled)).catch(() => {});
  }, [version.id, base, isDev]);

  const pending = desc?.published && Object.values(desc.translations || {}).some((tr) => tr.status === "pending");
  useEffect(() => {
    if (!pending) return undefined;
    const id = setInterval(() => api.get(`${base}/versions/${version.id}/description`).then(({ data }) => setDesc(data)), 3000);
    return () => clearInterval(id);
  }, [pending, version.id, base]);

  const attach = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    setFileError("");
    if (!file) return;
    if (!/\.(txt|md)$/i.test(file.name)) {
      setFileError(t("desc.fileError"));
      return;
    }
    const content = await file.text();
    touched.current = true;
    setText((cur) => insertAt(area.current, cur, content));
  };

  const publish = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`${base}/versions/${version.id}/description`, { text });
      setDesc(data);
      toast.success(t("desc.published"));
      onPublished();
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="card-carbon max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto rounded-2xl" data-testid="description-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider gold-text">{t("desc.title", { v: vLabel(version) })}</DialogTitle>
          <DialogDescription data-testid="desc-state">
            {desc?.published ? t("desc.publishedAt", { date: new Date(desc.published_at).toLocaleString(locale) }) : t("desc.notPublished")}
          </DialogDescription>
        </DialogHeader>
        {!desc ? <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" /> : (
          <div className="min-w-0 space-y-4">
            <TechInfo version={version} testId="desc-tech-info" />
            {desc.prefilled_from && <p className="text-xs text-[var(--gold)]" data-testid="desc-prefilled-note">{t("desc.prefilled", { v: vLabel({ version: desc.prefilled_from }) })}</p>}
            {ai && !isDev && <AiBlock versionId={version.id} onDraft={edit} t={t} />}
            <div className="flex flex-wrap gap-2">
              <input ref={fileInput} type="file" className="hidden" onChange={attach} data-testid="desc-file-input" />
              <button type="button" className="btn-red btn-sm" onClick={() => fileInput.current?.click()} data-testid="desc-attach-btn"><Paperclip className="h-4 w-4" /> {t("desc.attach")}</button>
              <DropdownMenu>
                <DropdownMenuTrigger className="btn-red btn-sm" data-testid="desc-insert-prompt-btn"><BookText className="h-4 w-4" /> {t("desc.insertPrompt")}</DropdownMenuTrigger>
                <DropdownMenuContent className="card-carbon max-w-[80vw] rounded-xl" data-testid="desc-prompt-menu">
                  {prompts.length === 0 && <DropdownMenuItem disabled>{t("desc.noPrompts")}</DropdownMenuItem>}
                  {prompts.map((p) => (
                    <DropdownMenuItem key={p.id} onSelect={() => edit(insertAt(area.current, text, p.text))} data-testid={`desc-prompt-option-${p.id}`}>
                      <span className="truncate">{p.title}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {fileError && <p className="flex items-center gap-2 text-sm text-red-200" role="alert" data-testid="desc-file-error"><AlertTriangle className="h-4 w-4" /> {fileError}</p>}
            <div className="space-y-2">
              <Label htmlFor="desc-text">{t("desc.editorLabel")}</Label>
              <Textarea id="desc-text" ref={area} rows={14} value={text} placeholder={t("desc.placeholder")} onChange={(e) => edit(e.target.value)} className="font-mono text-sm" data-testid="desc-textarea" />
            </div>
            <button type="button" className="btn-gold" onClick={publish} disabled={saving || !text.trim()} data-testid="desc-publish-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 rtl:-scale-x-100" />} {t("desc.publish")}
            </button>
            {desc.published && <TranslationsPanel versionId={version.id} desc={desc} onChange={setDesc} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
